/**
 * Reducer — JEDINÁ cesta k zmene stavu. Je čistý: rovnaký (state, action)
 * dá vždy rovnaký výsledok, takže server, klient aj replay sa nerozídu.
 *
 * Pravidlá, na ktoré sa tu odkazujem, sú vypísané v docs/PRAVIDLA.md.
 */

import type { Action } from './actions.js';
import { EFFECTS, HERO_SPECIALS } from './effects/index.js';
import type { CardEffect, EffectCtx } from './effects/types.js';
import { adjacentFighters, canMove } from './effects/helpers.js';
import { boostSources, setCancelHook } from './effects/helpers.js';
import type { GameEvent } from './events.js';
import { ACTIONS_PER_TURN, HAND_LIMIT } from './game.js';
import { isLegalPath, isLegalPathWithin } from './movement.js';
import { areAdjacent, neighbors } from './map.js';
import type { Ctx } from './ops.js';
import {
  RuleError,
  askChoice,
  cardDef,
  checkVictory,
  dealDamage,
  discardForBoost,
  discardFromHand,
  discardToDeck,
  drawCard,
  emit,
  endOngoing,
  getFighter,
  getMap,
  getPlayer,
  heroOf,
  defeatedFighter,
  limboFighter,
  moveFighter,
  swapFighters,
  moveToken,
  placeFighter,
  placeToken,
  putOnDeck,
  returnFromDiscard,
  searchDeck,
  tokensOf,
} from './ops.js';
import { areAllies, nextPlayer } from './players.js';
import { canAttackTarget } from './rules.js';
import type {
  ActionKind,
  CardDef,
  CardId,
  ContentIndex,
  Fighter,
  FighterId,
  FollowUp,
  GameState,
  PendingChoice,
  PlayerIndex,
  SpaceId,
} from './types.js';

export interface ReduceResult {
  state: GameState;
  events: GameEvent[];
}

export function reduce(state: GameState, action: Action, content: ContentIndex): ReduceResult {
  const ctx: Ctx = { state: structuredClone(state), content, events: [] };

  // Zápasy rozohrané pred Fázou 4 tieto polia nemajú.
  ctx.state.pending ??= [];
  ctx.state.turn.startSpaces ??= snapshotSpaces(ctx.state);

  if (ctx.state.phase === 'ended') throw new RuleError('Hra už skončila');

  switch (action.type) {
    case 'maneuver':
      applyManeuver(ctx, action);
      break;
    case 'maneuverBoost':
      applyManeuverBoost(ctx, action);
      break;
    case 'maneuverMove':
      applyManeuverMove(ctx, action);
      break;
    case 'maneuverEnd':
      applyManeuverEnd(ctx, action);
      break;
    case 'scheme':
      applyScheme(ctx, action);
      break;
    case 'attack':
      applyAttack(ctx, action);
      break;
    case 'defend':
      applyDefend(ctx, action);
      break;
    case 'discard':
      applyDiscard(ctx, action);
      break;
    case 'endTurn':
      applyEndTurn(ctx, action);
      break;
    case 'resolve':
      applyResolve(ctx, action);
      break;
    default: {
      const never: never = action;
      throw new RuleError(`Neznáma akcia: ${JSON.stringify(never)}`);
    }
  }

  settle(ctx);
  checkVictory(ctx);
  return { state: ctx.state, events: ctx.events };
}

/**
 * Upratanie po akcii: zahodí nevyriešiteľné voľby, spustí bonusový útok, keď
 * príde na rad, a dokončí súboj, ktorý čakal na rozhodnutie. Opakuje sa, kým
 * sa stav neustáli — každý krok môže vypýtať ďalšiu voľbu.
 */
function settle(ctx: Ctx): void {
  for (let guard = 0; guard < 200; guard++) {
    prunePending(ctx);
    if (ctx.state.phase === 'ended') return;
    const head = ctx.state.pending[0];
    if (head?.kind === 'bonusAttack') {
      ctx.state.pending.shift();
      startBonusAttack(ctx, head);
      continue;
    }
    if (head?.kind === 'auto') {
      ctx.state.pending.shift();
      runFollow(ctx, head, head.follow, {});
      continue;
    }
    const combat = ctx.state.combat;
    if (!head && combat?.step === 'resolve' && combat.stage) {
      continueCombat(ctx);
      continue;
    }
    if (!head && combat?.step === 'predict') {
      resolveCombat(ctx);
      continue;
    }
    return;
  }
  throw new RuleError('Efekty sa zacyklili');
}

// --- spoločné kontroly -----------------------------------------------------

function requireTurn(ctx: Ctx, player: PlayerIndex): void {
  requireNoPending(ctx);
  if (ctx.state.combat) throw new RuleError('Prebieha súboj');
  if (ctx.state.phase !== 'turn') throw new RuleError(`Nesprávna fáza: ${ctx.state.phase}`);
  if (ctx.state.turn.player !== player) throw new RuleError(`Nie je ťah hráča ${player}`);
}

/**
 * Rozhodnutie z efektu blokuje všetko ostatné — aj hráča na ťahu. Inak by sa
 * dal efekt „obísť" tým, že hráč spraví najprv niečo iné.
 */
function requireNoPending(ctx: Ctx): void {
  if (ctx.state.pending.length > 0) {
    throw new RuleError('Najprv dokonči prebiehajúci efekt karty');
  }
}

/**
 * Zahodí rozhodnutia, ktoré sa už nedajú vyriešiť. Medzi vypýtaním voľby
 * a odpoveďou sa doska mení — cieľ padne, priestor niekto obsadí — a pravidlá
 * hovoria jasne: čo sa vyriešiť nedá, sa proste nevyrieši, zvyšok platí.
 */
function prunePending(ctx: Ctx): void {
  while (ctx.state.pending.length > 0) {
    const choice = ctx.state.pending[0]!;
    if (choiceHasOptions(ctx, choice)) break;
    ctx.state.pending.shift();
    emit(ctx, { type: 'choiceSkipped', player: choice.player, label: choice.label });
    if (choice.onSkip) runFollow(ctx, choice, choice.onSkip, {});
  }
}

/** Má voľba ešte vôbec z čoho vyberať? */
function choiceHasOptions(ctx: Ctx, choice: PendingChoice): boolean {
  switch (choice.kind) {
    case 'move':
      return choice.fighters.some((id) => canMove(ctx, id, choice.max, choice.through));
    case 'place':
      return (
        (ctx.state.fighters.some((f) => f.id === choice.fighterId) ||
          Boolean(limboFighter(ctx.state, choice.fighterId)) ||
          Boolean(defeatedFighter(ctx.state, choice.fighterId))) &&
        choice.spaces.some((spaceId) => {
          const occupant = ctx.state.fighters.find((f) => f.spaceId === spaceId);
          return !occupant || canSwapOnPlace(ctx.state, choice, occupant);
        })
      );
    case 'damage':
      return choice.targets.some((id) => ctx.state.fighters.some((f) => f.id === id));
    case 'confirm':
    case 'option':
    case 'auto':
      return true;
    case 'swap':
      return (
        ctx.state.fighters.some((f) => f.id === choice.fighterId) &&
        choice.candidates.some((id) => ctx.state.fighters.some((f) => f.id === id))
      );
    case 'token':
      return choice.tokens.some((id) => tokenDestinations(ctx, choice, id).length > 0);
    case 'placeToken':
      return tokenPlacementSpaces(ctx, choice).length > 0;
    case 'card':
      return cardChoiceOptions(ctx, choice).length > 0;
    case 'bonusAttack':
      return (
        ctx.state.fighters.some((f) => f.id === choice.attackerFighter) &&
        ctx.state.fighters.some((f) => f.id === choice.defenderFighter)
      );
    default:
      return false;
  }
}

/** Kam smie ísť žetón pri danej voľbe. */
export function tokenDestinations(
  ctx: Ctx,
  choice: Extract<PendingChoice, { kind: 'token' }>,
  tokenId: string,
): SpaceId[] {
  const token = tokensOf(ctx.state).find((t) => t.id === tokenId);
  if (!token) return [];
  const map = getMap(ctx);
  let spaces: SpaceId[];
  if (choice.max === null) {
    spaces = map.spaces.map((s) => s.id);
  } else {
    // Žetón sa hýbe po čiarach mapy a smie prejsť aj cez figúrky.
    const seen = new Set<SpaceId>([token.spaceId]);
    let frontier = [token.spaceId];
    for (let step = 0; step < choice.max; step++) {
      const next: SpaceId[] = [];
      for (const current of frontier) {
        for (const neighbor of neighbors(map, current)) {
          if (seen.has(neighbor)) continue;
          seen.add(neighbor);
          next.push(neighbor);
        }
      }
      frontier = next;
    }
    spaces = [...seen];
  }
  return spaces.filter(
    (id) => id !== token.spaceId && (!choice.empty || !ctx.state.fighters.some((f) => f.spaceId === id)),
  );
}

/** Voľné priestory pre nový žetón hmly — bez figúrky a bez inej hmly. */
export function tokenPlacementSpaces(
  ctx: Ctx,
  choice: Extract<PendingChoice, { kind: 'placeToken' }>,
): SpaceId[] {
  return choice.spaces.filter(
    (id) =>
      !ctx.state.fighters.some((f) => f.spaceId === id) &&
      !tokensOf(ctx.state).some((t) => t.spaceId === id),
  );
}

/** Karty, z ktorých sa pri voľbe vyberá. Z ruky vždy tá aktuálna. */
export function cardChoiceOptions(ctx: Ctx, choice: Extract<PendingChoice, { kind: 'card' }>): CardId[] {
  const owner = getPlayer(ctx.state, choice.owner ?? choice.player);
  const keyword = choice.keyword;
  const matches = (cardId: CardId) =>
    !keyword || (EFFECTS[ctx.state.cards[cardId]!.defId]?.keywords ?? []).includes(keyword);
  if (choice.from === 'discard') {
    const pile = choice.cards ?? owner.discard;
    return pile.filter((id) => owner.discard.includes(id) && matches(id));
  }
  // Sight Beyond Sight: len nazreté karty z vrchu (`cards`), nie celý balíček.
  if (choice.from === 'deck') return owner.deck.filter((id) => matches(id) && (!choice.cards || choice.cards.includes(id)));
  // Boost smie ísť aj z Vibranium Suit (Black Panther).
  if (choice.purpose === 'boost') return boostSources(ctx, choice.owner ?? choice.player).filter(matches);
  const allowed = (cardId: CardId) =>
    matches(cardId) && (!choice.only || choice.only.includes(cardId)) && !choice.except?.includes(cardId);
  if (!choice.playableBy) return owner.hand.filter(allowed);
  // Sleight of Hand: len karta, ktorú smie táto figúrka zahrať ako útok.
  // Do My Bidding: smie ísť aj tá istá karta, ktorá práve leží v súboji.
  const fighter = ctx.state.fighters.find((f) => f.id === choice.playableBy);
  if (!fighter) return [];
  const current = ctx.state.combat?.attackCard;
  const pool = choice.purpose === 'replace' && current && ctx.state.cards[current]?.owner === owner.index
    ? [...owner.hand, current]
    : owner.hand;
  return pool.filter((cardId) => {
    const def = cardDef(ctx, cardId);
    if (def.type !== 'attack' && def.type !== 'versatile') return false;
    return bannerAllows(ctx, def, fighter);
  });
}

/**
 * Rozostavenie sidekickov: priestor s iným vlastným sidekickom sa smie zvoliť —
 * vymenia si miesta. Zóna hrdinu býva malá a bez výmeny by hráč niekedy
 * nemal kam kliknúť.
 */
export function canSwapOnPlace(
  state: GameState,
  choice: Extract<PendingChoice, { kind: 'place' }>,
  occupant: Fighter,
): boolean {
  if (!choice.swapOwn || occupant.id === choice.fighterId) return false;
  const fighter = state.fighters.find((f) => f.id === choice.fighterId);
  return Boolean(fighter && occupant.owner === fighter.owner && occupant.kind === 'sidekick');
}

/** Kde stála ktorá figúrka — snímka na začiatku ťahu. */
function snapshotSpaces(state: GameState): Record<FighterId, SpaceId> {
  return Object.fromEntries(state.fighters.map((f) => [f.id, f.spaceId]));
}

/**
 * Schopnosť hrdinu daného hráča. Berie sa podľa toho, koho si hráč vybral —
 * nie podľa figúrky na doske: schopnosť platí, aj keď je hrdina mimo dosky.
 */
function specialOf(ctx: Ctx, player: PlayerIndex) {
  const heroId = ctx.state.players[player]?.heroId;
  return heroId ? HERO_SPECIALS[heroId] : undefined;
}

/** Koľko akcií má hráč v tomto ťahu (2 + získané − prepadnuté). */
export function actionsAllowed(state: GameState): number {
  return ACTIONS_PER_TURN + (state.turn.extraActions ?? 0);
}

/** Bonus k pohybu z hrdinovej schopnosti (Sinbadove 7 VOYAGES). */
export function maneuverBonus(ctx: Ctx, player: PlayerIndex): number {
  return specialOf(ctx, player)?.maneuverBonus?.(ctx, player) ?? 0;
}

/**
 * Boost hodnota karty pre daného hráča — vytlačená plus bonus z trvalej schémy
 * (Advisor to the King: „Your BOOST values are +1").
 */
export function boostValue(ctx: Ctx, player: PlayerIndex, cardId: CardId): number {
  return cardDef(ctx, cardId).boost + (ongoingEffect(ctx, player)?.ongoing?.boostBonus ?? 0);
}

/** Efekt trvalej schémy, ktorá leží pred hráčom. */
export function ongoingEffect(ctx: Ctx, player: PlayerIndex): CardEffect | undefined {
  const cardId = ctx.state.players[player]?.ongoing;
  return cardId ? EFFECTS[ctx.state.cards[cardId]!.defId] : undefined;
}

/** Na ťahu sú presne dve akcie a smú byť aj dve rovnaké. */
function requireActionSlot(ctx: Ctx): void {
  if (ctx.state.maneuver) throw new RuleError('Najprv dokonči manéver');
  if (ctx.state.turn.actionsUsed.length >= actionsAllowed(ctx.state)) {
    throw new RuleError('Všetky akcie sú už minuté');
  }
}

function useActionSlot(ctx: Ctx, player: PlayerIndex, kind: ActionKind): void {
  ctx.state.turn.actionsUsed.push(kind);
  emit(ctx, { type: 'actionUsed', player, kind });
}

function requireOwnedCard(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  if (!getPlayer(ctx.state, player).hand.includes(cardId)) {
    throw new RuleError(`Karta ${cardId} nie je v ruke hráča ${player}`);
  }
}

/**
 * Smie figúrka kartu zahrať? Banner name (`character: null` = ktokoľvek) a nad
 * ním schopnosť hrdinu (`HeroSpecial.canPlay` — Jekyll/Hyde podľa formy).
 */
export function bannerAllows(ctx: Ctx, def: CardDef, fighter: Fighter): boolean {
  if (def.character && def.character !== fighter.defId) return false;
  return specialOf(ctx, fighter.owner)?.canPlay?.(ctx, fighter.owner, def, fighter) ?? true;
}

/**
 * Kartu smie zahrať len figúrka uvedená v banner name; `character: null` = ktokoľvek.
 * Kartu porazenej figúrky zahrať nemožno (na boost sa odhodiť smie).
 */
function requireBanner(ctx: Ctx, cardId: CardId, fighterId: string): void {
  const def = cardDef(ctx, cardId);
  const fighter = getFighter(ctx.state, fighterId);
  if (!bannerAllows(ctx, def, fighter)) {
    throw new RuleError(`Kartu ${def.name} teraz ${fighter.name} zahrať nesmie`);
  }
}

// --- manéver ---------------------------------------------------------------

function applyManeuver(ctx: Ctx, action: Extract<Action, { type: 'maneuver' }>): void {
  requireTurn(ctx, action.player);
  requireActionSlot(ctx);

  useActionSlot(ctx, action.player, 'maneuver');
  emit(ctx, { type: 'maneuverStarted', player: action.player });

  // Krok 1: ťah karty je POVINNÝ a ide pred pohybom (Hamlet TO BE: o kartu viac).
  const draws = 1 + (specialOf(ctx, action.player)?.maneuverDraw?.(ctx, action.player) ?? 0);
  for (let i = 0; i < draws; i++) drawCard(ctx, action.player);
  if (ctx.state.phase === 'ended') return;

  ctx.state.maneuver = {
    player: action.player,
    movedFighters: [],
    boostUsed: false,
    pendingBoost: 0,
  };
  specialOf(ctx, action.player)?.maneuverStart?.(ctx, action.player);
}

function requireManeuver(ctx: Ctx, player: PlayerIndex) {
  const maneuver = ctx.state.maneuver;
  if (!maneuver) throw new RuleError('Neprebieha žiadny manéver');
  if (maneuver.player !== player) throw new RuleError('Toto nie je tvoj manéver');
  return maneuver;
}

function applyManeuverBoost(ctx: Ctx, action: Extract<Action, { type: 'maneuverBoost' }>): void {
  const maneuver = requireManeuver(ctx, action.player);
  if (maneuver.boostUsed) throw new RuleError('Boost sa smie použiť raz za manéver');
  if (!boostSources(ctx, action.player).includes(action.cardId)) {
    throw new RuleError(`Kartou ${action.cardId} hráč ${action.player} boostovať nemôže`);
  }

  // Boost sa smie použiť len PRED pohybom figúrky, ktorej sa má týkať.
  const amount = boostValue(ctx, action.player, action.cardId);
  maneuver.boostUsed = true;
  maneuver.pendingBoost = amount;
  discardForBoost(ctx, action.player, action.cardId);
  emit(ctx, { type: 'maneuverBoosted', player: action.player, cardId: action.cardId, amount });
  triggerBoosted(ctx, action.player, action.cardId);
}

/** „BOOSTED WITH:" — efekt karty odhodenej za boost (Houdini). */
function triggerBoosted(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const effect = EFFECTS[ctx.state.cards[cardId]!.defId];
  effect?.boosted?.(effectCtx(ctx, cardId, player, heroOf(ctx.state, player)?.id ?? null, 'boost', null, null, false));
  // Black Panther: „Whenever you BOOST, draw 1 card."
  specialOf(ctx, player)?.onBoost?.(ctx, player);
}

function applyManeuverMove(ctx: Ctx, action: Extract<Action, { type: 'maneuverMove' }>): void {
  const maneuver = requireManeuver(ctx, action.player);
  const fighter = getFighter(ctx.state, action.fighterId);
  if (fighter.owner !== action.player) throw new RuleError('Cudzia figúrka');
  if (maneuver.movedFighters.includes(fighter.id)) {
    throw new RuleError(`${fighter.name} sa už v tomto manévri pohol`);
  }
  const extra = maneuver.pendingBoost + maneuverBonus(ctx, action.player);
  const placing = canManeuverPlace(ctx, action.player, fighter, action.path);
  if (!placing && !isLegalPath(getMap(ctx), ctx.state, fighter, action.path, extra)) {
    throw new RuleError(`Neplatná cesta pre ${fighter.name}: ${action.path.join(' → ')}`);
  }

  maneuver.movedFighters.push(fighter.id);
  maneuver.pendingBoost = 0; // boost platí len na jeden presun
  if (placing) placeFighter(ctx, fighter.id, action.path[1]!);
  else moveFighter(ctx, fighter.id, action.path);
}

/**
 * Houdiniho ESCAPE ARTIST: po booste v manévri sa smie namiesto pohybu
 * položiť na ľubovoľný prázdny priestor. Cesta má vtedy tvar [odkiaľ, kam].
 */
export function canManeuverPlace(
  ctx: Ctx,
  player: PlayerIndex,
  fighter: Fighter,
  path: readonly SpaceId[],
): boolean {
  if (path.length !== 2 || path[0] !== fighter.spaceId) return false;
  if (!ctx.state.maneuver?.boostUsed) return false;
  if (!specialOf(ctx, player)?.maneuverPlace?.(ctx, player, fighter)) return false;
  return !ctx.state.fighters.some((f) => f.spaceId === path[1]);
}

function applyManeuverEnd(ctx: Ctx, action: Extract<Action, { type: 'maneuverEnd' }>): void {
  requireManeuver(ctx, action.player);
  ctx.state.maneuver = null;
  emit(ctx, { type: 'maneuverEnded', player: action.player });
  specialOf(ctx, action.player)?.afterManeuver?.(ctx, action.player);
}

// --- plán ------------------------------------------------------------------

function applyScheme(ctx: Ctx, action: Extract<Action, { type: 'scheme' }>): void {
  requireTurn(ctx, action.player);
  requireActionSlot(ctx);
  requireOwnedCard(ctx, action.player, action.cardId);

  const def = cardDef(ctx, action.cardId);
  if (def.type !== 'scheme') throw new RuleError(`Karta ${def.name} nie je plán`);

  const fighter = getFighter(ctx.state, action.fighterId);
  if (fighter.owner !== action.player) throw new RuleError('Cudzia figúrka');
  requireBanner(ctx, action.cardId, action.fighterId);

  useActionSlot(ctx, action.player, 'scheme');
  // Karta sa zahrá, vyrieši a až potom ide do odhadzovacieho balíčka.
  const effect = EFFECTS[ctx.state.cards[action.cardId]!.defId];
  effect?.scheme?.(
    effectCtx(ctx, action.cardId, action.player, action.fighterId, 'scheme', null, null, false),
  );
  if (effect?.ongoing) {
    // Trvalá schéma ostane ležať; predchádzajúca (najviac jedna naraz) ide do odhodu.
    endOngoing(ctx, action.player);
    const player = getPlayer(ctx.state, action.player);
    player.hand.splice(player.hand.indexOf(action.cardId), 1);
    player.ongoing = action.cardId;
    emit(ctx, { type: 'ongoingPlayed', player: action.player, cardId: action.cardId });
  } else {
    discardFromHand(ctx, action.player, action.cardId, 'played');
  }
  // Triss: po vyriešení schémy 2 poškodenia susednej figúrke.
  if (ctx.state.fighters.some((f) => f.id === action.fighterId)) {
    specialOf(ctx, action.player)?.afterScheme?.(ctx, action.player, action.fighterId);
  }
}

// --- útok ------------------------------------------------------------------

function applyAttack(ctx: Ctx, action: Extract<Action, { type: 'attack' }>): void {
  requireTurn(ctx, action.player);
  requireActionSlot(ctx);
  requireOwnedCard(ctx, action.player, action.cardId);

  const attacker = getFighter(ctx.state, action.fighterId);
  const target = getFighter(ctx.state, action.targetId);
  if (attacker.owner !== action.player) throw new RuleError('Cudzia figúrka');
  if (areAllies(ctx.state, target.owner, action.player)) {
    throw new RuleError('Na spojencove figúrky sa útočiť nedá');
  }
  const effect = EFFECTS[ctx.state.cards[action.cardId]!.defId];
  const inRange = canAttackTarget(ctx.state, getMap(ctx), attacker, target);
  // Foul Purpose: cieľ kdekoľvek, ale karta sa hrá lícom hore.
  const anywhere = !inRange && Boolean(effect?.attackAnywhere?.(ctx, action.player, attacker));
  if (!inRange && !anywhere) {
    throw new RuleError(`${attacker.name} nedosiahne na ${target.name}`);
  }
  // Witness My Last Battle: len na susednú figúrku, lícom hore.
  if (effect?.adjacentOnly && !areAdjacent(getMap(ctx), attacker.spaceId, target.spaceId)) {
    throw new RuleError(`Táto karta útočí len na susednú figúrku`);
  }
  const faceUp = anywhere || Boolean(effect?.playFaceUp);

  if (!canPlayAsAttack(ctx, action.player, action.cardId, attacker)) {
    throw new RuleError(`Karta ${cardDef(ctx, action.cardId).name} sa nedá použiť ako útok`);
  }
  requireBanner(ctx, action.cardId, attacker.id);

  useActionSlot(ctx, action.player, 'attack');
  // Kto už v tomto ťahu útočil (Ancient Leshen: +3 pri ďalšom útoku).
  (ctx.state.turn.attackers ??= []).push(attacker.id);
  const hand = getPlayer(ctx.state, action.player).hand;
  hand.splice(hand.indexOf(action.cardId), 1);

  openCombat(ctx, attacker.id, target.id, action.cardId, undefined, faceUp);
}

/** Útok alebo univerzálna karta — a obranná, ak to jej efekt dovolí (Implacable). */
export function canPlayAsAttack(ctx: Ctx, player: PlayerIndex, cardId: CardId, attacker: Fighter): boolean {
  const def = cardDef(ctx, cardId);
  if (def.type === 'attack' || def.type === 'versatile') return true;
  return Boolean(EFFECTS[def.id]?.playableAsAttack?.(ctx, player, attacker));
}

/**
 * Otvorenie súboja — aj pre bonusový útok. Ak obranca nemá čím reagovať,
 * súboj sa vyrieši hneď, nikto nečaká.
 */
function openCombat(
  ctx: Ctx,
  attackerId: FighterId,
  defenderId: FighterId,
  cardId: CardId,
  bonus: { name: string; value: number } | undefined,
  faceUp = false,
): void {
  const attacker = getFighter(ctx.state, attackerId);
  const target = getFighter(ctx.state, defenderId);
  ctx.state.combat = {
    attackerFighter: attacker.id,
    defenderFighter: target.id,
    attackerPlayer: attacker.owner,
    defenderPlayer: target.owner,
    attackCard: cardId,
    defenseCard: null,
    defenderPassed: false,
    step: 'respond',
    ...(bonus ? { bonus } : {}),
    ...(faceUp ? { faceUp } : {}),
  };
  emit(ctx, {
    type: 'combatStarted',
    attackerFighter: attacker.id,
    defenderFighter: target.id,
    attackerPlayer: attacker.owner,
    defenderPlayer: target.owner,
    ...(bonus ? { bonus: bonus.name } : {}),
  });
  // Spidey-Sense: útočník povie vytlačenú hodnotu, kým si obranca vyberá kartu.
  const defenderSpecial = specialOf(ctx, target.owner);
  if (target.kind === 'hero' && defenderSpecial?.hearsAttackValue) {
    const announced = bonus ? bonus.value : cardDef(ctx, cardId).value;
    ctx.state.combat.announced = announced;
    emit(ctx, {
      type: 'specialUsed',
      player: target.owner,
      heroId: target.defId,
      name: `${defenderSpecial.name}: útok má vytlačenú hodnotu ${announced}`,
    });
  }

  if (playableDefenses(ctx, target.owner).length === 0) {
    ctx.state.combat.defenderPassed = true;
    resolveCombat(ctx);
  }
}

/**
 * Bonusový útok (Relentless Assault): ten istý útočník proti tomu istému
 * obrancovi, aj keď už nie je v dosahu. Nemíňa akciu. Karta je už v odhode —
 * hrá sa „ako nová útočná karta" s vlastným menom a hodnotou, bez efektu.
 * (Battle of Legends Vol. 2, „Bonus attacks")
 */
function startBonusAttack(ctx: Ctx, choice: Extract<PendingChoice, { kind: 'bonusAttack' }>): void {
  if (!choice.source) return;
  openCombat(ctx, choice.attackerFighter, choice.defenderFighter, choice.source, {
    name: choice.name,
    value: choice.value,
  });
}

export function playableDefenses(ctx: Ctx, player: PlayerIndex): CardId[] {
  const combat = ctx.state.combat;
  const defender = combat ? getFighter(ctx.state, combat.defenderFighter) : null;
  return getPlayer(ctx.state, player).hand.filter((cardId) => {
    const def = cardDef(ctx, cardId);
    if (def.type !== 'defense' && def.type !== 'versatile') return false;
    if (defender && !bannerAllows(ctx, def, defender)) return false;
    return true;
  });
}

function applyDefend(ctx: Ctx, action: Extract<Action, { type: 'defend' }>): void {
  const combat = ctx.state.combat;
  if (!combat) throw new RuleError('Neprebieha žiadny súboj');
  if (combat.step !== 'respond') throw new RuleError('Na obranu je neskoro');
  if (combat.defenderPlayer !== action.player) throw new RuleError('Nie si obranca');

  if (action.cardId === null) {
    combat.defenderPassed = true;
  } else {
    requireOwnedCard(ctx, action.player, action.cardId);
    if (!playableDefenses(ctx, action.player).includes(action.cardId)) {
      throw new RuleError('Táto karta sa nedá použiť na obranu');
    }
    const hand = getPlayer(ctx.state, action.player).hand;
    hand.splice(hand.indexOf(action.cardId), 1);
    combat.defenseCard = action.cardId;
    // Elementary: tip pred odhalením — súboj počká, `settle` ho potom odhalí.
    const effect = EFFECTS[ctx.state.cards[action.cardId]!.defId];
    if (effect?.beforeReveal) {
      combat.step = 'predict';
      effect.beforeReveal(
        effectCtx(ctx, action.cardId, action.player, combat.defenderFighter, 'defender', combat.attackerPlayer, combat.attackerFighter, false),
      );
      if (ctx.state.pending.length > 0) return;
    }
  }
  resolveCombat(ctx);
}

/**
 * Vyhodnotenie súboja v poradí podľa pravidiel:
 * IMMEDIATELY → DURING COMBAT → poškodenie → AFTER COMBAT → cleanup.
 *
 * Efekt si môže uprostred vypýtať rozhodnutie (Under Achilles' Helm, The Day
 * of Your Doom). Súboj si vtedy zapamätá krok (`combat.stage`), vráti sa
 * a `settle` ho dokončí, keď hráč odpovie.
 */
function resolveCombat(ctx: Ctx): void {
  const combat = ctx.state.combat;
  if (!combat || !combat.attackCard) throw new RuleError('Nekompletný súboj');

  combat.step = 'reveal';
  emit(ctx, {
    type: 'combatCardsRevealed',
    attackCard: combat.attackCard,
    defenseCard: combat.defenseCard,
  });

  combat.step = 'resolve';
  combat.stage = 'immediate';
  combat.cancelled = { attacker: false, defender: false };
  combat.flags = [];
  continueCombat(ctx);
}

/** Efekty oboch kariet v súboji. Bonusový útok nemá efekt žiadny. */
function combatEffects(ctx: Ctx) {
  const combat = ctx.state.combat!;
  const attackEffect = combat.bonus ? undefined : EFFECTS[ctx.state.cards[combat.attackCard!]!.defId];
  const defenseEffect = combat.defenseCard
    ? EFFECTS[ctx.state.cards[combat.defenseCard]!.defId]
    : undefined;
  const attackerSide = (won = false) =>
    effectCtx(
      ctx,
      combat.attackCard!,
      combat.attackerPlayer,
      combat.attackerFighter,
      'attacker',
      combat.defenderPlayer,
      combat.defenderFighter,
      won,
    );
  const defenderSide = (won = false) =>
    effectCtx(
      ctx,
      combat.defenseCard!,
      combat.defenderPlayer,
      combat.defenderFighter,
      'defender',
      combat.attackerPlayer,
      combat.attackerFighter,
      won,
    );
  return { attackEffect, defenseEffect, attackerSide, defenderSide };
}

function continueCombat(ctx: Ctx): void {
  const combat = ctx.state.combat;
  if (!combat || combat.step !== 'resolve') return;
  const { attackEffect, defenseEffect, attackerSide, defenderSide } = combatEffects(ctx);
  const cancelled = (combat.cancelled ??= { attacker: false, defender: false });

  // 1. IMMEDIATELY — pri zhode ide prvý obranca (docs/PRAVIDLA.md) a jeho
  //    efekt sa dohrá celý, aj s rozhodnutím (Telepathy), skôr než príde na
  //    rad útočník. Zrušenie je tiež „immediate", takže obrancov Feint stihne
  //    zrušiť útočníkovu kartu ešte pred tým, než sa jej efekt spustí.
  if (combat.stage === 'immediate') {
    combat.stage = 'immediateAttacker';
    if (defenseEffect && combat.defenseCard) {
      const cancels = defenseEffect.cancelsOpponent || defenseEffect.cancelsOpponentIf?.(defenderSide());
      // Zrušenie sa zapíše aj pri karte bez efektu — King Arthur podľa neho
      // vie, že jeho boost prepadol. Bonusový útok efekt nemá vôbec.
      if (cancels && combat.attackCard && !combat.bonus) cancelCombatCard(ctx, 'attacker');
      defenseEffect.immediate?.(defenderSide());
    }
    if (ctx.state.pending.length > 0) return;
  }
  if (combat.stage === 'immediateAttacker') {
    combat.stage = 'during';
    // Yennefer: „IMMEDIATELY: If Yennefer is attacking, you may BOOST her attack."
    // Je to schopnosť, nie karta — zrušiť sa nedá a platí aj pri zrušenej karte.
    if (!combat.bonus) {
      specialOf(ctx, combat.attackerPlayer)?.attackImmediate?.(ctx, combat.attackerPlayer, combat.attackerFighter);
    }
    if (attackEffect && !cancelled.attacker) {
      const cancels = attackEffect.cancelsOpponent || attackEffect.cancelsOpponentIf?.(attackerSide());
      if (cancels && combat.defenseCard) cancelCombatCard(ctx, 'defender');
      attackEffect.immediate?.(attackerSide());
    }
    if (ctx.state.pending.length > 0) return;
  }

  // 2a. DURING COMBAT — rozhodnutia („You may…"). Obranca prvý.
  if (combat.stage === 'during') {
    combat.stage = 'values';
    if (defenseEffect?.duringChoice && !cancelled.defender && combat.defenseCard) {
      defenseEffect.duringChoice(defenderSide());
    }
    if (attackEffect?.duringChoice && !cancelled.attacker) {
      attackEffect.duringChoice(attackerSide());
    }
    if (ctx.state.pending.length > 0) return;
  }

  finishCombat(ctx);
}

/**
 * Zrušenie efektov karty v súboji — ak sa zrušiť dajú. Nedá sa karta
 * s „This card's effects cannot be canceled" ani karta Ciri so 7+ Source
 * kartami v odhode.
 */
export function cancelCombatCard(ctx: Ctx, side: 'attacker' | 'defender'): void {
  const combat = ctx.state.combat;
  if (!combat) return;
  const cardId = side === 'attacker' ? (combat.bonus ? null : combat.attackCard) : combat.defenseCard;
  const player = side === 'attacker' ? combat.attackerPlayer : combat.defenderPlayer;
  const cancelled = (combat.cancelled ??= { attacker: false, defender: false });
  if (!cardId || cancelled[side]) return;
  if (EFFECTS[ctx.state.cards[cardId]!.defId]?.uncancelable) return;
  if (specialOf(ctx, player)?.cardsUncancelable?.(ctx, player, cardId)) return;
  cancelled[side] = true;
  emit(ctx, { type: 'effectCancelled', player, cardId });
}

// Telepathy ruší súperovu kartu mimo kroku zrušení — cez tú istú kontrolu.
setCancelHook(cancelCombatCard);

function finishCombat(ctx: Ctx): void {
  const combat = ctx.state.combat!;
  const { attackEffect, defenseEffect, attackerSide, defenderSide } = combatEffects(ctx);
  const cancelled = combat.cancelled ?? { attacker: false, defender: false };
  const flags = combat.flags ?? [];
  delete combat.stage;

  const attackCard = combat.attackCard!;
  const defenseCard = combat.defenseCard;
  const attackerPlayer = combat.attackerPlayer;
  const defenderPlayer = combat.defenderPlayer;

  // 2b. DURING COMBAT — efekty menia hodnotu VLASTNEJ karty.
  // This Is No Parlor Trick: hodnota súperovej karty je jej BOOST.
  const printedAttack = flags.includes('boostValue:attacker')
    ? cardDef(ctx, attackCard).boost
    : combat.bonus ? combat.bonus.value : cardDef(ctx, attackCard).value;
  let attackValue = printedAttack;
  if (attackEffect?.during && !cancelled.attacker) {
    attackValue = attackEffect.during(attackerSide(), attackValue);
  }
  attackValue += specialOf(ctx, attackerPlayer)?.attackModifier?.(ctx, attackerPlayer, alive(ctx, combat.attackerFighter)) ?? 0;
  attackValue += combat.boosts?.attacker ?? 0;

  const printedDefense = defenseCard
    ? flags.includes('boostValue:defender') ? cardDef(ctx, defenseCard).boost : cardDef(ctx, defenseCard).value
    : 0;
  let defenseValue = printedDefense;
  if (defenseCard) {
    if (defenseEffect?.during && !cancelled.defender) {
      defenseValue = defenseEffect.during(defenderSide(), defenseValue);
    }
    defenseValue +=
      specialOf(ctx, defenderPlayer)?.defenseModifier?.(ctx, defenderPlayer, alive(ctx, combat.defenderFighter)) ?? 0;
    defenseValue += combat.boosts?.defender ?? 0;
  }
  // „Impossible to See", „ignore the value": hodnota je 0 a efekty ju už nezmenia.
  if (flags.includes('zeroAttack')) attackValue = 0;
  if (flags.includes('zeroDefense')) defenseValue = 0;
  // Paralyzing Fetters: „equal to its printed value and cannot be changed" — ani vynulovať.
  if (flags.includes('printedAttack')) attackValue = printedAttack;
  if (flags.includes('printedDefense')) defenseValue = printedDefense;
  // Three Wishes: „your cards' values are 4 and cannot be changed" — platí nad všetkým ostatným.
  const fixed = ctx.state.turn.fixedValue;
  if (fixed?.player === attackerPlayer) attackValue = fixed.value;
  if (fixed?.player === defenderPlayer && defenseCard) defenseValue = fixed.value;
  attackValue = Math.max(0, attackValue);
  defenseValue = Math.max(0, defenseValue);

  // 3. Poškodenie. Obranca útočníkovi combat damage nikdy nespôsobí.
  //    Obranca sa mohol medzitým zmeniť (Patroklos prevzal súboj).
  const rawDamage = Math.max(0, attackValue - defenseValue);
  // Luke Cage: o 2 menej combat damage. Kto nedostal nič, súboj vyhral (aj bez karty).
  let damage =
    specialOf(ctx, defenderPlayer)?.combatDamageTaken?.(ctx, defenderPlayer, combat.defenderFighter, rawDamage) ??
    rawDamage;
  // Web Shooters, Deeds of Valor: „cannot take more than 2 combat damage this combat".
  const cap = flags.find((f) => f.startsWith('damageCap:'));
  if (cap) damage = Math.min(damage, Number(cap.slice('damageCap:'.length)));
  // Golden Chain Mail: combat damage, ktoré by dostal obranca, dostane útočník.
  const reflected = flags.includes('reflect') && !cancelled.defender ? damage : 0;
  if (reflected) damage = 0;
  emit(ctx, { type: 'combatValues', attack: attackValue, defense: defenseValue, damage });
  combat.values = { attack: attackValue, defense: defenseValue };
  const target = ctx.state.fighters.find((f) => f.id === combat.defenderFighter);
  // All Part of the Show: smrteľný zásah figúrku nezloží, zdravie sa nastaví.
  const rescue =
    target && damage >= target.hp && defenseEffect?.surviveLethal && !cancelled.defender
      ? defenseEffect.surviveLethal(defenderSide())
      : null;
  if (target && rescue !== null) {
    emit(ctx, { type: 'damageDealt', fighterId: target.id, amount: target.hp, hpAfter: 0 });
    target.hp = rescue;
    emit(ctx, { type: 'fighterRevived', fighterId: target.id, hp: rescue });
  } else {
    dealDamage(ctx, combat.defenderFighter, damage, 'combat');
  }
  if (reflected) dealDamage(ctx, combat.attackerFighter, reflected, 'combat');

  // Kto vyhral: útočník, ak spôsobil aspoň 1 combat damage; obranca, ak žiadny
  // nedostal. Poškodenie z efektov sa do toho nepočíta.
  const attackerWon = damage >= 1;
  (ctx.state.turn.wins ??= []).push(attackerWon ? attackerPlayer : defenderPlayer);

  // 4. AFTER COMBAT — opäť obranca prvý. Efekt sa vyrieši aj vtedy, keď bola
  //    figúrka medzitým porazená (vtedy je `fighterId` null).
  if (defenseEffect?.after && !cancelled.defender && defenseCard) {
    defenseEffect.after({ ...defenderSide(!attackerWon), damage });
  }
  if (attackEffect?.after && !cancelled.attacker) {
    attackEffect.after({ ...attackerSide(attackerWon), damage });
  }
  // Schopnosti „ak vyhrá súboj" idú až po efektoch kariet.
  specialOf(ctx, defenderPlayer)?.afterCombat?.(ctx, defenderPlayer, alive(ctx, combat.defenderFighter), !attackerWon);
  specialOf(ctx, attackerPlayer)?.afterCombat?.(ctx, attackerPlayer, alive(ctx, combat.attackerFighter), attackerWon);

  // 5. Cleanup: zahrané karty do odhadzovacích balíčkov (aj keď hra skončila).
  //    Karta bonusového útoku v odhode už je.
  if (!combat.bonus) {
    getPlayer(ctx.state, attackerPlayer).discard.push(attackCard);
    emit(ctx, { type: 'cardDiscarded', player: attackerPlayer, cardId: attackCard, reason: 'played' });
  }
  if (defenseCard) {
    getPlayer(ctx.state, defenderPlayer).discard.push(defenseCard);
    emit(ctx, { type: 'cardDiscarded', player: defenderPlayer, cardId: defenseCard, reason: 'played' });
  }

  ctx.state.combat = null;
  emit(ctx, { type: 'combatEnded' });

  // Trvalé schémy „after each combat your fighter is in" (Unicorn Ally).
  for (const player of new Set([attackerPlayer, defenderPlayer])) {
    ongoingEffect(ctx, player)?.ongoing?.afterCombat?.(ctx, player);
  }
}

function alive(ctx: Ctx, id: FighterId): FighterId | null {
  return ctx.state.fighters.some((f) => f.id === id) ? id : null;
}

/** Kontext pre efekt karty. Porazená figúrka sa nahradí `null`. */
function effectCtx(
  ctx: Ctx,
  cardId: CardId,
  player: PlayerIndex,
  fighterId: FighterId | null,
  role: EffectCtx['role'],
  opponentPlayer: PlayerIndex | null,
  opponentFighterId: FighterId | null,
  won: boolean,
): EffectCtx {
  const alive = (id: FighterId | null) =>
    id && ctx.state.fighters.some((f) => f.id === id) ? id : null;
  return {
    ctx,
    cardId,
    player,
    fighterId: alive(fighterId),
    role,
    opponentPlayer,
    opponentFighterId: alive(opponentFighterId),
    won,
  };
}

// --- rozhodnutia z efektov -------------------------------------------------

function applyResolve(ctx: Ctx, action: Extract<Action, { type: 'resolve' }>): void {
  const choice = ctx.state.pending[0];
  if (!choice) throw new RuleError('Nič sa práve nerozhoduje');
  if (choice.player !== action.player) throw new RuleError('Toto rozhodnutie nie je tvoje');

  if (action.choice.kind === 'skip') {
    if (!choice.optional) throw new RuleError('Tento efekt sa preskočiť nedá');
    ctx.state.pending.shift();
    emit(ctx, { type: 'choiceSkipped', player: choice.player, label: choice.label });
    if (choice.onSkip) runFollow(ctx, choice, choice.onSkip, {});
    return;
  }

  // Bonusový útok spúšťa `settle` sám — hráč naň neodpovedá, takže
  // žiadna odpoveď sa s ním nezhoduje.
  if (action.choice.kind !== choice.kind) {
    throw new RuleError(`Na voľbu „${choice.kind}" prišla odpoveď „${action.choice.kind}"`);
  }

  switch (choice.kind) {
    case 'move': {
      const answer = action.choice as { kind: 'move'; fighterId: FighterId; path: SpaceId[] };
      if (!choice.fighters.includes(answer.fighterId)) {
        throw new RuleError('Túto figúrku efekt nepresúva');
      }
      const fighter = getFighter(ctx.state, answer.fighterId);
      if (!isLegalPathWithin(getMap(ctx), ctx.state, fighter, answer.path, choice.max, choice.through)) {
        throw new RuleError(`Neplatná cesta pre ${fighter.name}`);
      }
      ctx.state.pending.shift();
      moveFighter(ctx, answer.fighterId, answer.path);
      if (choice.follow) runFollow(ctx, choice, choice.follow, { fighterId: answer.fighterId, path: answer.path });
      return;
    }
    case 'place': {
      const answer = action.choice as { kind: 'place'; spaceId: SpaceId };
      const occupant = ctx.state.fighters.find((f) => f.spaceId === answer.spaceId);
      const swap = occupant && canSwapOnPlace(ctx.state, choice, occupant);
      if (!choice.spaces.includes(answer.spaceId) || (occupant && !swap)) {
        throw new RuleError('Sem sa figúrka položiť nedá');
      }
      ctx.state.pending.shift();
      if (swap) swapFighters(ctx, choice.fighterId, occupant.id);
      else placeFighter(ctx, choice.fighterId, answer.spaceId);
      return;
    }
    case 'damage': {
      const answer = action.choice as { kind: 'damage'; targetId: FighterId };
      if (!choice.targets.includes(answer.targetId)) throw new RuleError('Tento cieľ efekt nezasiahne');
      ctx.state.pending.shift();
      dealDamage(ctx, answer.targetId, choice.amount);
      return;
    }
    case 'confirm': {
      ctx.state.pending.shift();
      const special = choice.special ? HERO_SPECIALS[choice.special] : undefined;
      if (special) {
        emit(ctx, {
          type: 'specialUsed',
          player: choice.player,
          heroId: choice.special!,
          name: special.name,
        });
        special.onConfirm?.(ctx, choice.player);
      }
      if (choice.effect) EFFECTS[choice.effect]?.onConfirm?.(ctx, choice);
      return;
    }
    case 'token': {
      const answer = action.choice as { kind: 'token'; tokenId: string; spaceId: SpaceId };
      if (!choice.tokens.includes(answer.tokenId)) throw new RuleError('Tento žetón efekt nepresúva');
      if (!tokenDestinations(ctx, choice, answer.tokenId).includes(answer.spaceId)) {
        throw new RuleError('Sem sa žetón presunúť nedá');
      }
      ctx.state.pending.shift();
      moveToken(ctx, answer.tokenId, answer.spaceId);
      if (choice.follow) runFollow(ctx, choice, choice.follow, { tokenId: answer.tokenId });
      return;
    }
    case 'placeToken': {
      const answer = action.choice as { kind: 'placeToken'; spaceId: SpaceId };
      if (!tokenPlacementSpaces(ctx, choice).includes(answer.spaceId)) {
        throw new RuleError('Sem sa hmla položiť nedá');
      }
      ctx.state.pending.shift();
      placeToken(ctx, choice.player, answer.spaceId);
      // Hmla položená pred prvou akciou hry patrí do snímky „začiatku ťahu".
      if (ctx.state.turn.actionsUsed.length === 0) {
        ctx.state.turn.startTokens = snapshotTokens(ctx.state);
      }
      return;
    }
    case 'card': {
      const answer = action.choice as { kind: 'card'; cardId: CardId };
      if (!cardChoiceOptions(ctx, choice).includes(answer.cardId)) {
        throw new RuleError('Túto kartu vybrať nemožno');
      }
      ctx.state.pending.shift();
      const owner = choice.owner ?? choice.player;
      switch (choice.purpose) {
        case 'toHand':
          if (choice.from === 'deck') searchDeck(ctx, owner, answer.cardId);
          else returnFromDiscard(ctx, owner, [answer.cardId]);
          break;
        case 'topOfDeck':
          if (choice.from === 'discard') discardToDeck(ctx, owner, answer.cardId);
          else putOnDeck(ctx, owner, answer.cardId);
          break;
        case 'effect': {
          const source = choice.source ? ctx.state.cards[choice.source]?.defId : undefined;
          if (source) EFFECTS[source]?.onCardChosen?.(ctx, choice, answer.cardId);
          break;
        }
        case 'boost':
          applyCombatBoost(ctx, owner, answer.cardId, choice.side ?? 'attacker');
          break;
        case 'replace':
          replaceAttackCard(ctx, owner, answer.cardId);
          break;
        default: {
          // Odhodil ho súperov efekt, keď karta, ktorá voľbu vyvolala, patrí inému hráčovi.
          const sourceOwner = choice.source ? ctx.state.cards[choice.source]?.owner : undefined;
          discardFromHand(ctx, owner, answer.cardId, 'effect', sourceOwner !== undefined && sourceOwner !== owner);
        }
      }
      return;
    }
    case 'swap': {
      const answer = action.choice as { kind: 'swap'; targetId: FighterId };
      if (!choice.candidates.includes(answer.targetId)) throw new RuleError('S touto figúrkou sa vymeniť nedá');
      ctx.state.pending.shift();
      swapFighters(ctx, choice.fighterId, answer.targetId);
      // Kto si vymenil miesto so súbojom, prevezme v ňom úlohu — okrem výmeny,
      // ktorá úlohy nemení (Fearsome Strength).
      const combat = choice.keepRoles ? null : ctx.state.combat;
      if (combat?.defenderFighter === choice.fighterId) {
        combat.defenderFighter = answer.targetId;
        emit(ctx, { type: 'defenderChanged', fighterId: answer.targetId });
      } else if (combat?.attackerFighter === choice.fighterId) {
        combat.attackerFighter = answer.targetId;
        emit(ctx, { type: 'attackerChanged', fighterId: answer.targetId });
      }
      return;
    }
    case 'option': {
      const answer = action.choice as { kind: 'option'; index: number };
      const picked = choice.options[answer.index];
      if (!picked) throw new RuleError('Taká možnosť nie je');
      ctx.state.pending.shift();
      runFollow(ctx, choice, picked.follow, {});
      return;
    }
    default: {
      const never: never = choice;
      throw new RuleError(`Neznáme rozhodnutie: ${JSON.stringify(never)}`);
    }
  }
}

/**
 * Pokračovanie po vyriešenej voľbe. Ciele sa dajú vymenovať až teraz —
 * Jackalope Horns bije susedov NOVEJ pozície, Slip Away kladie hrdinu tam,
 * kam práve prišla hmla. Pokračovanie ide **pred** ostatné čakajúce voľby.
 */
function runFollow(
  ctx: Ctx,
  choice: PendingChoice,
  follow: FollowUp,
  context: { fighterId?: FighterId; tokenId?: string; path?: SpaceId[] },
): void {
  const next: PendingChoice[] = [];
  const base = { source: choice.source } as const;
  const tokens = tokensOf(ctx.state).filter((t) => t.kind === 'fog');

  switch (follow.kind) {
    case 'damageAdjacent': {
      const fighter = context.fighterId
        ? ctx.state.fighters.find((f) => f.id === context.fighterId)
        : undefined;
      if (!fighter) return;
      const targets = adjacentFighters(ctx, fighter.spaceId);
      if (targets.length === 0) return;
      next.push({
        ...base,
        kind: 'damage',
        player: choice.player,
        label: follow.label,
        optional: false,
        amount: follow.amount,
        targets,
      });
      break;
    }
    case 'moveToken': {
      const ids = tokens
        .filter((t) => !(follow.exceptMoved && t.id === context.tokenId))
        .map((t) => t.id);
      if (ids.length === 0) return;
      next.push({
        ...base,
        kind: 'token',
        player: follow.player,
        label: follow.label,
        optional: follow.optional,
        tokens: ids,
        max: follow.max,
        empty: false,
      });
      break;
    }
    case 'placeHeroOnToken': {
      const token = tokens.find((t) => t.id === context.tokenId);
      const hero = heroOf(ctx.state, choice.player);
      if (!token || !hero || ctx.state.fighters.some((f) => f.spaceId === token.spaceId)) return;
      placeFighter(ctx, hero.id, token.spaceId);
      return;
    }
    case 'placeHeroOnFog': {
      const hero = heroOf(ctx.state, choice.player);
      if (!hero) return;
      const spaces = tokens
        .filter((t) => t.owner === choice.player)
        .map((t) => t.spaceId)
        .filter((id) => id !== hero.spaceId && !ctx.state.fighters.some((f) => f.spaceId === id));
      if (spaces.length === 0) return;
      next.push({
        ...base,
        kind: 'place',
        player: choice.player,
        label: `Presuň ${hero.name} na priestor s hmlou`,
        optional: false,
        fighterId: hero.id,
        spaces: [...new Set(spaces)],
      });
      break;
    }
    case 'effect': {
      EFFECTS[follow.effect]?.onFollow?.(ctx, choice, follow.data, context);
      return;
    }
    case 'special': {
      // `_draw` = obyčajné ťahanie kariet po návrate figúrky na dosku.
      if (follow.hero === '_draw') {
        for (let i = 0; i < Number(follow.data); i++) drawCard(ctx, choice.player);
        return;
      }
      HERO_SPECIALS[follow.hero]?.onFollow?.(ctx, choice.player, follow.data, context);
      return;
    }
    case 'damageThrough': {
      // Súperove figúrky na priestoroch, cez ktoré cesta viedla (bez začiatku a cieľa).
      const through = new Set((context.path ?? []).slice(1, -1));
      const targets = ctx.state.fighters
        .filter((f) => through.has(f.spaceId) && !areAllies(ctx.state, f.owner, choice.player))
        .map((f) => f.id);
      if (follow.each) {
        // Power Man: presunutá figúrka aj každý, cez koho prešla.
        const all = [...new Set([...(context.fighterId ? [context.fighterId] : []), ...targets])];
        for (const id of all) dealDamage(ctx, id, follow.amount);
        return;
      }
      if (targets.length === 0) return;
      next.push({
        ...base,
        kind: 'damage',
        player: choice.player,
        label: follow.label,
        optional: false,
        amount: follow.amount,
        targets,
      });
      break;
    }
    case 'moveEveryToken': {
      for (const token of tokens) {
        next.push({
          ...base,
          kind: 'token',
          player: follow.player,
          label: 'Smieš presunúť túto hmlu na ľubovoľný iný priestor',
          optional: true,
          tokens: [token.id],
          max: null,
          empty: false,
        });
      }
      break;
    }
    default: {
      const never: never = follow;
      throw new RuleError(`Neznáme pokračovanie: ${JSON.stringify(never)}`);
    }
  }
  ctx.state.pending.unshift(...next);
}

/**
 * BOOST počas súboja („You may BOOST this attack"): karta z ruky ide do
 * odhodu a jej boost sa pripočíta k hodnote vlastnej karty v súboji.
 * Odhodená karta spustí svoj „BOOSTED WITH" efekt (Houdini).
 */
function applyCombatBoost(ctx: Ctx, player: PlayerIndex, cardId: CardId, side: 'attacker' | 'defender'): void {
  const combat = ctx.state.combat;
  if (!combat) return;
  const amount = boostValue(ctx, player, cardId);
  discardForBoost(ctx, player, cardId);
  combat.boosts ??= { attacker: 0, defender: 0 };
  combat.boostCards ??= { attacker: [], defender: [] };
  combat.boosts[side] += amount;
  combat.boostCards[side].push(cardId);
  emit(ctx, { type: 'maneuverBoosted', player, cardId, amount });
  triggerBoosted(ctx, player, cardId);
}

/**
 * Sleight of Hand: „vráť túto kartu do ruky a zahraj inú; jej efekty sa
 * vyriešia normálne". Nová karta dostane aj svoj IMMEDIATE — ten už prebehol
 * pre pôvodnú kartu.
 */
function replaceAttackCard(ctx: Ctx, player: PlayerIndex, cardId: CardId): void {
  const combat = ctx.state.combat;
  if (!combat?.attackCard) return;
  const hand = getPlayer(ctx.state, player).hand;
  const old = combat.attackCard;
  if (cardId !== old) {
    hand.splice(hand.indexOf(cardId), 1);
    hand.push(old);
    combat.attackCard = cardId;
  }
  emit(ctx, { type: 'attackCardReplaced', player, from: old, to: cardId });

  // Výmena v kroku obrancu (Do My Bidding): IMMEDIATELY novej karty ešte len
  // príde na rad v kroku útočníka — nesmie sa spustiť dvakrát.
  if (combat.stage === 'immediateAttacker') return;
  const { attackEffect, attackerSide } = combatEffects(ctx);
  if (attackEffect) {
    const cancels = attackEffect.cancelsOpponent || attackEffect.cancelsOpponentIf?.(attackerSide());
    if (cancels && combat.defenseCard) cancelCombatCard(ctx, 'defender');
    attackEffect.immediate?.(attackerSide());
  }
}

// --- koniec ťahu -----------------------------------------------------------

function applyDiscard(ctx: Ctx, action: Extract<Action, { type: 'discard' }>): void {
  requireTurn(ctx, action.player);
  const hand = getPlayer(ctx.state, action.player).hand;
  if (hand.length <= HAND_LIMIT) throw new RuleError('Limit ruky nie je prekročený');
  discardFromHand(ctx, action.player, action.cardId, 'handLimit');
}

function applyEndTurn(ctx: Ctx, action: Extract<Action, { type: 'endTurn' }>): void {
  requireTurn(ctx, action.player);
  if (ctx.state.maneuver) throw new RuleError('Najprv dokonči manéver');

  const used = ctx.state.turn.actionsUsed.length;
  const allowed = actionsAllowed(ctx.state);
  if (used < allowed) {
    throw new RuleError(`Musíš vykonať ${allowed} akcie (máš ${used})`);
  }

  /*
   * Schopnosti „na konci ťahu" idú PRED odhadzovaním na limit ruky — Bigfoot
   * si smie potiahnuť a až potom rieši, či nemá priveľa kariet. Ak si schopnosť
   * vypýta rozhodnutie, ťah sa zastaví; hráč odpovie a ukončí ho znova.
   * Príznak `ending` stráži, aby sa schopnosť nespustila druhýkrát.
   */
  if (!ctx.state.turn.ending) {
    ctx.state.turn.ending = true;
    specialOf(ctx, action.player)?.endOfTurn?.(ctx, action.player);
    if (ctx.state.pending.length > 0) return;
    // Schopnosť dotiahla kartu nad limit (T. Rex) — najprv odhadzovanie, potom
    // hráč ukončí ťah znova (príznak `ending` stráži, aby sa neťahalo druhýkrát).
    if (getPlayer(ctx.state, action.player).hand.length > HAND_LIMIT) return;
  }

  const player = getPlayer(ctx.state, action.player);
  const over = player.hand.length - HAND_LIMIT;
  if (over > 0) throw new RuleError(`Najprv odhoď ${over} kariet (limit ruky ${HAND_LIMIT})`);

  // Trvalá schéma: podmienka na odhodenie sa kontroluje len na konci vlastného ťahu.
  if (ongoingEffect(ctx, action.player)?.ongoing?.discardAtEnd(ctx, action.player)) {
    endOngoing(ctx, action.player);
  }

  emit(ctx, { type: 'turnEnded', player: action.player });
  // Vypadnutí hráči sa preskakujú.
  const next = nextPlayer(ctx.state, action.player);
  ctx.state.turn = {
    player: next,
    actionsUsed: [],
    number: ctx.state.turn.number + 1,
    startSpaces: snapshotSpaces(ctx.state),
    startTokens: snapshotTokens(ctx.state),
  };
  emit(ctx, { type: 'turnStarted', player: next, turnNumber: ctx.state.turn.number });

  startTurnEffects(ctx, next);
}

/**
 * Čo sa deje na začiatku ťahu: návrat figúrky mimo dosky (Vanish) a
 * schopnosti „at the start of your turn" (Medusa).
 */
export function startTurnEffects(ctx: Ctx, next: PlayerIndex): void {
  // Figúrka mimo dosky (Vanish) sa vracia na začiatku ťahu svojho hráča.
  for (const away of (ctx.state.limbo ?? []).filter((f) => f.owner === next)) {
    askChoice(ctx, {
      kind: 'place',
      player: next,
      source: null,
      label: `Polož ${away.name} na ľubovoľný voľný priestor`,
      optional: false,
      fighterId: away.id,
      spaces: getMap(ctx).spaces.map((space) => space.id),
    });
    // Vanish into Murder: „…place the Leshen in any space. Then, draw 1 card."
    if (away.returnDraw) {
      askChoice(ctx, {
        kind: 'auto',
        player: next,
        source: null,
        label: 'Návrat na dosku: ťah kariet',
        optional: false,
        follow: { kind: 'special', hero: '_draw', data: String(away.returnDraw) },
      });
    }
  }
  specialOf(ctx, next)?.startOfTurn?.(ctx, next);
  ongoingEffect(ctx, next)?.ongoing?.startOfTurn?.(ctx, next);
}

/** Kde ležia žetóny — snímka na začiatku ťahu. */
export function snapshotTokens(state: GameState): Record<string, SpaceId> {
  return Object.fromEntries(tokensOf(state).map((t) => [t.id, t.spaceId]));
}
